import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Wallet, RefreshCw, AlertCircle, Landmark, Banknote, Printer, FileSpreadsheet, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { exportToCSV, printTable } from '../../lib/printUtils';
import { fmtCLP, refrescarDesdeAlegra } from './egresos/egresosUtils';
import { EgresosTable } from './egresos/EgresosTable';
import { EgresoCrearDialog } from './egresos/EgresoCrearDialog';
import { EgresoConciliarDialog } from './egresos/EgresoConciliarDialog';
import { EgresoTrazabilidadSheet } from './egresos/EgresoTrazabilidadSheet';

const hoyISO = () => new Date().toISOString().slice(0, 10);

function Kpi({ icon: Icon, color, label, value, sub, testId, onClick, active }) {
  return (
    <Card data-testid={testId} onClick={onClick} className={`${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${active ? 'ring-2 ring-indigo-400' : ''}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color.bg}`}><Icon className={`h-5 w-5 ${color.fg}`} /></div>
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground">{label}</p>
          <p className={`text-2xl font-bold tabular-nums ${color.text || ''}`}>{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground truncate">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function EgresosPage() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState(null);
  const [bancos, setBancos] = useState([]);
  const [bancoPrincipalId, setBancoPrincipalId] = useState(null);
  const [fAplicacion, setFAplicacion] = useState('');
  const [fBanco, setFBanco] = useState('');
  const [fTipo, setFTipo] = useState('');
  const [fQ, setFQ] = useState(searchParams.get('q') || '');
  const [fOC, setFOC] = useState(searchParams.get('oc') || '');
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [refrescandoId, setRefrescandoId] = useState(null);
  const [conciliar, setConciliar] = useState(null);
  const [traza, setTraza] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fAplicacion) params.set('estado_aplicacion', fAplicacion);
      if (fBanco) params.set('estado_conciliacion', fBanco);
      if (fTipo) params.set('tipo', fTipo);
      if (fQ.trim()) params.set('q', fQ.trim());
      if (fOC.trim()) params.set('oc_numero', fOC.trim());
      const mov = searchParams.get('movimiento');
      if (mov) params.set('movimiento_id', mov);
      const [k, l, b] = await Promise.all([
        api.get('/contabilidad/egresos/kpis'),
        api.get(`/contabilidad/egresos?${params.toString()}`),
        api.get('/contabilidad/egresos/bancos'),
      ]);
      setKpis(k.data || null);
      setItems(l.data?.items || []);
      setTotal(l.data?.total ?? 0);
      setBancos(b.data?.bancos || []);
      setBancoPrincipalId(b.data?.principal_id || null);
    } catch {
      toast.error('No se pudieron cargar los egresos');
    } finally { setLoading(false); }
  }, [fAplicacion, fBanco, fTipo, fQ, fOC, searchParams]);

  useEffect(() => { const t = setTimeout(loadAll, 200); return () => clearTimeout(t); }, [loadAll]);

  // Iter 396 (Renzo): tras crear, mostrar SOLO el egreso nuevo (ordenado por fecha de pago se perdía entre los demás).
  const handleCreated = (eg) => {
    const clave = eg?.alegra_number || eg?.codigo_conciliacion || '';
    if (!clave) { loadAll(); return; }
    setFAplicacion(''); setFBanco(''); setFTipo(''); setFOC('');
    setFQ(clave);
    toast.info(`Mostrando solo el egreso N°${clave} recién creado · limpia el filtro de búsqueda para ver todos`, { duration: 8000 });
  };

  const handleCancel = async (e) => {
    if (!window.confirm(`¿Cancelar egreso ${e.codigo_conciliacion} de ${fmtCLP(e.monto)}? Esta acción NO toca Alegra.`)) return;
    try { await api.delete(`/contabilidad/egresos/${e.id}`); toast.success('Egreso cancelado localmente'); loadAll(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Error al cancelar'); }
  };

  const handleDesconciliar = async (e) => {
    if (!window.confirm(`¿Desconciliar ${e.codigo_conciliacion}? El movimiento de la cartola vuelve a pendiente. No afecta la aplicación a facturas.`)) return;
    try { await api.post(`/contabilidad/egresos/${e.id}/desconciliar-banco`); toast.success('Egreso desconciliado'); loadAll(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const handleRefrescar = async (e) => {
    setRefrescandoId(e.id);
    if (await refrescarDesdeAlegra('egresos', e)) loadAll();
    setRefrescandoId(null);
  };

  const handleSyncAlegra = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/contabilidad/egresos/sync-alegra?dias_back=60');
      if (res.data?.already_running) toast.info('Ya hay una sincronización en curso; esperando que termine…');
      else toast.info('Sincronizando egresos con Alegra en segundo plano…');
      const inicio = res.data?.run_started_at || null;
      await new Promise((resolve) => {
        const t = setInterval(async () => {
          try {
            const st = (await api.get('/contabilidad/egresos/sync-alegra/status')).data || {};
            if (st.running || !st.last_run || (inicio && st.last_run < inicio)) return;
            clearInterval(t);
            if (st.last_error) toast.error(`Sync Alegra falló: ${st.last_error}`);
            else {
              const r = st.last_summary || {};
              toast.success(`Sync completo: ${r.insertados ?? 0} nuevos, ${r.actualizados ?? 0} actualizados (${r.alegra_outs_found ?? 0} egresos en Alegra)`);
            }
            resolve();
          } catch { /* reintentar */ }
        }, 3000);
      });
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error sincronizando con Alegra'); }
    finally { setSyncing(false); }
  };

  const handleEditarOC = async (e) => {
    const nuevo = window.prompt(`N° de OC asociada al egreso ${e.codigo_conciliacion} de ${e.razon_social} (${fmtCLP(e.monto)}).\nAl contabilizar la factura de esa OC se aplicará automáticamente en Alegra.`, e.oc_numero || '');
    if (nuevo === null) return;
    try {
      await api.patch(`/contabilidad/egresos/${e.id}`, { oc_numero: nuevo.trim() });
      toast.success(nuevo.trim() ? `Egreso asociado a OC ${nuevo.trim()}` : 'OC desasociada');
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'No se pudo actualizar la OC'); }
  };

  const columnas = [
    { key: 'fecha_pago', label: 'Fecha pago', format: fmtFecha },
    { key: 'fecha_cartola_esperada', label: 'Cartola esp.', format: fmtFecha },
    { key: 'codigo_conciliacion', label: 'Código', mono: true },
    { key: 'rut_proveedor', label: 'RUT', mono: true },
    { key: 'razon_social', label: 'Proveedor' },
    { key: 'nombre_conciliacion', label: 'Nombre conciliación', mono: true },
    { key: 'oc_numero', label: 'OC', align: 'center' },
    { key: 'monto', label: 'Monto', align: 'right', mono: true, format: (v) => fmtCLP(v) },
    { key: 'monto_disponible', label: 'Disponible', align: 'right', mono: true, format: (v) => fmtCLP(v) },
    { key: 'estado_aplicacion', label: 'Aplicación', align: 'center', format: (v) => (v || '').toUpperCase() },
    { key: 'estado_conciliacion_banco', label: 'Banco', align: 'center', format: (v) => (v || '').toUpperCase() },
  ];
  const imprimir = () => {
    if (!items.length) { toast.warning('No hay egresos para imprimir'); return; }
    printTable({ title: 'Egresos', subtitle: `${items.length} egresos · Generado ${new Date().toLocaleString('es-CL')}`, columns: columnas, rows: items });
  };
  const exportar = () => {
    if (!items.length) { toast.warning('No hay egresos para exportar'); return; }
    exportToCSV({ filename: 'egresos', title: 'CHIRINO STEEL · Egresos', columns: [...columnas, { key: 'alegra_payment_id', label: 'ID Pago Alegra' }, { key: 'descripcion', label: 'Descripción' }], rows: items });
  };

  const con = kpis?.conciliacion || {};
  return (
    <div className="space-y-5 page-enter" data-testid="egresos-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Wallet className="h-6 w-6 text-indigo-600" /> Egresos
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Cada egreso vive en dos procesos independientes: <b>aplicación</b> a la factura que llegue después (anticipos) y
            <b> conciliación</b> con el movimiento real de la cartola bancaria. Ninguno modifica al otro.
          </p>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <Button variant="outline" size="sm" onClick={imprimir} data-testid="btn-print-egresos"><Printer className="h-3.5 w-3.5 mr-1" /> Imprimir</Button>
          <Button variant="outline" size="sm" onClick={exportar} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="btn-excel-egresos"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel</Button>
          <Button variant="outline" size="sm" onClick={handleSyncAlegra} disabled={syncing} className="gap-2" data-testid="btn-sync-alegra">
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Sincronizando…' : 'Sync Alegra (60d)'}
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="btn-nuevo-egreso"><Plus className="h-4 w-4" /> Nuevo egreso</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi icon={Wallet} color={{ bg: 'bg-amber-500/10', fg: 'text-amber-600' }} label="Anticipos por aplicar" value={(kpis?.pendientes_count ?? 0) + (kpis?.parciales_count ?? 0)} sub={fmtCLP(kpis?.monto_total_disponible)} testId="kpi-pendientes"
          onClick={() => setFAplicacion(fAplicacion === 'pendiente,parcial' ? '' : 'pendiente,parcial')} active={fAplicacion === 'pendiente,parcial'} />
        <Kpi icon={Banknote} color={{ bg: 'bg-emerald-500/10', fg: 'text-emerald-600', text: 'text-emerald-700' }} label="Saldo disponible" value={fmtCLP(kpis?.monto_total_disponible)} testId="kpi-disponible" />
        <Kpi icon={AlertCircle} color={{ bg: 'bg-red-500/10', fg: 'text-red-600', text: 'text-red-600' }} label="+60d sin aplicar" value={kpis?.anticipos_60d_sin_aplicar ?? 0} testId="kpi-viejo" />
        <Kpi icon={Landmark} color={{ bg: 'bg-sky-500/10', fg: 'text-sky-600' }} label="Banco: pendientes" value={con.pendiente?.count ?? 0} sub={`${kpis?.conciliacion_atrasados ?? 0} ya debieran estar en cartola`} testId="kpi-banco-pendientes"
          onClick={() => setFBanco(fBanco === 'pendiente' ? '' : 'pendiente')} active={fBanco === 'pendiente'} />
        <Kpi icon={Eye} color={{ bg: 'bg-orange-500/10', fg: 'text-orange-600', text: 'text-orange-700' }} label="Banco: observados" value={con.observado?.count ?? 0} sub="Revisión manual (P3)" testId="kpi-banco-observados"
          onClick={() => setFBanco(fBanco === 'observado' ? '' : 'observado')} active={fBanco === 'observado'} />
        <Kpi icon={Landmark} color={{ bg: 'bg-emerald-500/10', fg: 'text-emerald-600', text: 'text-emerald-700' }} label="Banco: conciliados" value={con.conciliado?.count ?? 0} sub={fmtCLP(con.conciliado?.monto)} testId="kpi-banco-conciliados"
          onClick={() => setFBanco(fBanco === 'conciliado' ? '' : 'conciliado')} active={fBanco === 'conciliado'} />
      </div>

      <Card>
        <CardContent className="p-4 flex gap-3 flex-wrap items-center">
          <div className="flex items-center gap-2">
            <Label className="text-xs">Aplicación:</Label>
            <Select value={fAplicacion || 'todos'} onValueChange={(v) => setFAplicacion(v === 'todos' ? '' : v)}>
              <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filtro-aplicacion"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="pendiente,parcial">Por aplicar</SelectItem>
                <SelectItem value="pendiente">Pendientes</SelectItem>
                <SelectItem value="parcial">Parciales</SelectItem>
                <SelectItem value="aplicado">Aplicados</SelectItem>
                <SelectItem value="no_aplica">Gasto directo</SelectItem>
                <SelectItem value="cancelado">Cancelados</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Banco:</Label>
            <Select value={fBanco || 'todos'} onValueChange={(v) => setFBanco(v === 'todos' ? '' : v)}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="filtro-banco"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="pendiente">Pendientes</SelectItem>
                <SelectItem value="observado">Observados</SelectItem>
                <SelectItem value="conciliado">Conciliados</SelectItem>
                <SelectItem value="no_aplica">Sin cartola</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Tipo:</Label>
            <Select value={fTipo || 'todos'} onValueChange={(v) => setFTipo(v === 'todos' ? '' : v)}>
              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="filtro-tipo"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="anticipo">Anticipos</SelectItem>
                <SelectItem value="pago_factura">Pagos de factura</SelectItem>
                <SelectItem value="gasto">Gastos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="relative">
            <Input placeholder="Buscar proveedor, RUT, N° egreso, código…" value={fQ} onChange={(e) => setFQ(e.target.value)} className="h-8 w-[240px] pr-7 text-xs" data-testid="filtro-q" />
            {fQ && <button type="button" onClick={() => setFQ('')} className="absolute right-1.5 top-1.5 text-xs text-muted-foreground hover:text-foreground" title="Limpiar búsqueda" data-testid="filtro-q-limpiar">✕</button>}
          </div>
          <Input placeholder="N° OC" value={fOC} onChange={(e) => setFOC(e.target.value)} className="h-8 w-[100px] text-xs" data-testid="filtro-oc" />
          <span className="ml-auto text-xs text-muted-foreground" data-testid="egresos-count">{items.length < total ? `Mostrando ${items.length} de ${total}` : `${total} egresos`}</span>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Egresos {loading ? '(cargando…)' : ''}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {items.length === 0 ? (
            <p className="text-xs text-muted-foreground py-8 text-center" data-testid="egresos-empty">
              {fAplicacion || fBanco || fTipo || fQ || fOC ? 'Sin resultados con los filtros actuales.' : 'No hay egresos. Usa "Sync Alegra" para traer los pagos realizados o crea uno nuevo.'}
            </p>
          ) : (
            <EgresosTable items={items} hoy={hoyISO()} onEditarOC={handleEditarOC} onConciliar={setConciliar} onDesconciliar={handleDesconciliar} onTrazabilidad={setTraza} onCancelar={handleCancel} onRefrescar={handleRefrescar} refrescandoId={refrescandoId} />
          )}
        </CardContent>
      </Card>

      <EgresoCrearDialog open={showCreate} onOpenChange={setShowCreate} bancos={bancos} bancoPrincipalId={bancoPrincipalId} onCreated={handleCreated} />
      <EgresoConciliarDialog egreso={conciliar} onOpenChange={(o) => !o && setConciliar(null)} onDone={loadAll} />
      <EgresoTrazabilidadSheet egreso={traza} onOpenChange={(o) => !o && setTraza(null)} />
    </div>
  );
}
